import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Home, Menu, X } from "lucide-react";
import { Flourish } from "@/components/Common";

export const NAV = [
  { to: "/", label: "ہوم" },
  { to: "/urdu-adab", label: "اردو ادب" },
  { to: "/authors", label: "شعراء و ادباء" },
  { to: "/genres", label: "اصنافِ ادب" },
  { to: "/history", label: "تاریخِ ادب" },
  { to: "/articles", label: "تنقید و تحقیق" },
  { to: "/media", label: "اردو میڈیا" },
  { to: "/journals", label: "اردو رسائل و جرائد" },
  { to: "/ugc-net", label: "UGC NET" },
  { to: "/about", label: "ادب نامہ کے بارے میں" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      setOpen(false);
      setQ("");
    }
  };

  return (
    <header className="bg-[#2a1508] text-[#f5efe3] sticky top-0 z-50 shadow-[0_2px_12px_rgba(26,13,5,0.4)]">
      <div className="border-b border-[#d4af37]/20">
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-between text-xs sm:text-sm text-[#e8dfcc]">
          <span className="leading-[2]">علم سے چراغ جلتا ہے، ادب سے دل روشن ہوتا ہے</span>
          <span className="hidden sm:block leading-[2]">اردو زبان، ہماری پہچان — ہمارا فخر</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 pt-4 pb-2 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <button
            data-testid="header-search-toggle"
            onClick={() => setOpen(!open)}
            className="w-10 h-10 rounded-lg border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37] hover:bg-[#4a2b16] transition-colors"
            aria-label="تلاش"
          >
            <Search size={18} />
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            className="sm:hidden w-10 h-10 rounded-lg border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37] hover:bg-[#4a2b16] transition-colors"
            aria-label="مینیو"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        <Link to="/" data-testid="header-logo" className="text-center group">
          <div className="flex items-center gap-3 justify-center">
            <Flourish className="w-10 text-[#c5a059] rotate-180 hidden sm:block" />
            <span className="text-3xl sm:text-4xl font-bold text-[#f5efe3] leading-[1.6] group-hover:text-[#d4af37] transition-colors">ادب نامہ</span>
            <Flourish className="w-10 text-[#c5a059] hidden sm:block" />
          </div>
          <span className="font-latin text-[0.65rem] tracking-[0.5em] text-[#c5a059] uppercase">Adabnama</span>
        </Link>

        <Link
          to="/"
          data-testid="header-home-button"
          className="w-10 h-10 rounded-lg border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37] hover:bg-[#4a2b16] transition-colors"
          aria-label="ہوم"
        >
          <Home size={18} />
        </Link>
      </div>

      {open && (
        <div className="max-w-7xl mx-auto px-4 pb-3">
          <form onSubmit={submit} className="flex items-center gap-2 bg-[#fdfbf7] rounded-full pe-2 ps-5 py-1.5 border border-[#c5a059]">
            <input
              data-testid="header-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="شاعری، کتاب، ادیب، مضمون تلاش کریں…"
              className="flex-1 bg-transparent outline-none text-[#2a1508] text-base leading-[2]"
              autoFocus
            />
            <button data-testid="header-search-submit" className="w-9 h-9 rounded-full bg-[#2a1508] text-[#f5efe3] flex items-center justify-center hover:bg-[#4a2b16] transition-colors">
              <Search size={16} />
            </button>
          </form>
        </div>
      )}

      <nav className="max-w-7xl mx-auto px-4 pb-3 overflow-x-auto hidden sm:block" data-testid="main-nav">
        <div className="flex items-center gap-1 sm:gap-2 justify-start sm:justify-center min-w-max">
          {NAV.map((n, i) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${i}`}
              className={({ isActive }) =>
                `px-3 py-1.5 rounded-full text-sm sm:text-base whitespace-nowrap transition-colors leading-[2] ${
                  isActive ? "bg-[#d4af37] text-[#2a1508] font-semibold" : "text-[#e8dfcc] hover:bg-[#4a2b16]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
      </nav>

      {menuOpen && (
        <nav className="sm:hidden border-t border-[#d4af37]/20 px-4 py-3" data-testid="mobile-nav">
          <div className="flex flex-wrap gap-2 justify-center">
            {NAV.map((n, i) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setMenuOpen(false)}
                data-testid={`mobile-nav-link-${i}`}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors leading-[2] ${
                    isActive ? "bg-[#d4af37] text-[#2a1508] font-semibold" : "text-[#e8dfcc] bg-[#4a2b16]/60"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
};
